/* Agregar citas a un calendario personal (TD-11766):
 *  - urlGoogleCalendar: abre Google Calendar con UN evento ya completado (la
 *    persona solo toca "Guardar"). No usa la API ni pide permisos.
 *  - descargarIcs: archivo .ics con uno o varios eventos, para importar en
 *    Google Calendar, Outlook, el iPhone, etc.
 * Es una copia: si después se cancela o reprograma la cita, el evento del
 * calendario no se actualiza solo (al volver a importar un .ics, los eventos
 * con el mismo UID se reemplazan en lugar de duplicarse en la mayoría de los
 * calendarios).
 */
import { guardarTexto, sanitizarNombre } from "src/utils/archivos";
import { fechaDeTextoSql } from "src/utils/fechas";

export interface EventoCalendario {
  /** Identificador estable (el mismo evento no se duplica al importarlo dos veces). */
  uid: string;
  titulo: string;
  /** 'YYYY-MM-DD HH:MM[:SS]' en hora local, como lo devuelve el backend. */
  inicio: string;
  fin: string;
  lugar?: string | null;
  descripcion?: string | null;
}

/** Date -> 'YYYYMMDDTHHMMSSZ' (UTC): Google y los .ics lo interpretan igual en cualquier zona. */
const aUtc = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const fechas = (ev: EventoCalendario) => {
  const inicio = fechaDeTextoSql(ev.inicio);
  const fin = fechaDeTextoSql(ev.fin);
  if (!inicio || !fin) throw new Error("Fecha de la cita inválida");
  return { inicio: aUtc(inicio), fin: aUtc(fin) };
};

export function urlGoogleCalendar(ev: EventoCalendario): string {
  const { inicio, fin } = fechas(ev);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.titulo,
    dates: `${inicio}/${fin}`,
    details: ev.descripcion || "",
    location: ev.lugar || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Texto para un campo de iCalendar (RFC 5545 §3.3.11). */
const escaparIcs = (texto: string) =>
  texto.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

/** Corta las líneas largas en 75 bytes UTF-8 (RFC 5545 §3.1): una letra con
 *  tilde ocupa 2 bytes, así que no alcanza con contar caracteres. */
const plegar = (linea: string) => {
  const codificador = new TextEncoder();
  const partes: string[] = [];
  let actual = "";
  let bytes = 0;
  for (const caracter of Array.from(linea)) {
    const largo = codificador.encode(caracter).length;
    // La primera línea admite 75 bytes; las siguientes, 74 + el espacio inicial.
    if (bytes + largo > 75) {
      partes.push(actual);
      actual = " ";
      bytes = 1;
    }
    actual += caracter;
    bytes += largo;
  }
  partes.push(actual);
  return partes.join("\r\n");
};

const lineasEvento = (ev: EventoCalendario, dtstamp: string) => {
  const { inicio, fin } = fechas(ev);
  return [
    "BEGIN:VEVENT",
    `UID:${ev.uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${inicio}`,
    `DTEND:${fin}`,
    `SUMMARY:${escaparIcs(ev.titulo)}`,
    ...(ev.lugar ? [`LOCATION:${escaparIcs(ev.lugar)}`] : []),
    ...(ev.descripcion ? [`DESCRIPTION:${escaparIcs(ev.descripcion)}`] : []),
    // Aviso del calendario una hora antes (además de los recordatorios de la app).
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escaparIcs(ev.titulo)}`,
    "TRIGGER:-PT1H",
    "END:VALARM",
    "END:VEVENT",
  ];
};

/** Archivo .ics con uno o varios eventos. Con `nombre`, los calendarios que lo
 *  soportan lo muestran como nombre del calendario importado. */
export function contenidoIcs(eventos: EventoCalendario | EventoCalendario[], nombre?: string): string {
  const lista = Array.isArray(eventos) ? eventos : [eventos];
  const dtstamp = aUtc(new Date());
  const lineas = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//El Mejor//Agendamientos//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    ...(nombre ? [`X-WR-CALNAME:${escaparIcs(nombre)}`] : []),
    ...lista.flatMap((ev) => lineasEvento(ev, dtstamp)),
    "END:VCALENDAR",
  ];
  return lineas.map(plegar).join("\r\n") + "\r\n";
}

/** Descarga (o comparte, en nativo) uno o varios eventos como .ics. */
export function descargarIcs(eventos: EventoCalendario | EventoCalendario[], nombre?: string): Promise<void> {
  const lista = Array.isArray(eventos) ? eventos : [eventos];
  const archivo = `${sanitizarNombre(nombre || lista[0]?.titulo || "agenda")}.ics`;
  return guardarTexto(archivo, contenidoIcs(lista, nombre), "text/calendar;charset=utf-8", "Agregar al calendario");
}
